<template>
  <svg ref="svg" :width="width" :height="height"></svg>
</template>

<script>
import * as d3 from 'd3';

const arrowHead = (function() {
  const markerBoxWidth = 10;
  const markerBoxHeight = 6;
  return {
    markerBoxWidth,
    markerBoxHeight,
    refX: markerBoxWidth, 
    refY: markerBoxHeight / 2,
    markerWidth: markerBoxWidth / 2,
    markerHeight: markerBoxHeight / 2,
    arrowPoints: [[0, 0], [0, markerBoxHeight], [markerBoxWidth, markerBoxHeight * 0.5]],
  }
})();

// Configuration objects used by rendering functions
// stdPaths: Standard support symbols (roller, hinge, fixed)
// strokes: Default stroke colors for shape types
// strokeWidths: Default stroke widths for shape types  
// fills: Default fill colors for shape types

function defineArrowHead(defs) {
  const {
    markerBoxWidth,
    markerBoxHeight,
    refX,
    refY,
    // markerWidth and markerHeight are part of arrowHead config
    // but not directly used in this function
    // markerWidth,
    // markerHeight,
    arrowPoints
  } = arrowHead;
  // Arrow Head
  defs
    .append('marker')
    .attr('id', 'arrow')
    .attr('viewBox', [0, 0, markerBoxWidth, markerBoxHeight])
    .attr('refX', refX)
    .attr('refY', refY)
    .attr('markerWidth', markerBoxWidth)
    .attr('markerHeight', markerBoxHeight)
    .attr('orient', 'auto-start-reverse')
    .append('path')
    .attr('d', d3.line()(arrowPoints))
    .attr('stroke', 'black');
}

function defineDiagonalHatch(defs) {
  // Diagonal Hatch
  defs
    .append('pattern')
    .attr('id', 'diagonalHatch')
    .attr('patternUnits', "userSpaceOnUse")
    .attr('width', 4)
    .attr('height', 4)
    .append("path")
    .attr("d", "M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2")
    .attr('stroke', 'black')
    .attr("stroke-width", 1);
}

const camelToDash = (str) => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase();
};

const basicShapeTypes = ['line', 'rect', 'circle', 'polygon', 'text', 'path'];

const parser = {
  parse: function (data) {
    return data.reduce((acc, d) => {
      // Don't change the original data
      const cloned = Object.assign({}, d);
      // For each applicable rule, call the callback using the value of target key/s
      this.rules.forEach((rule) => {
        if (d.type === rule.appliesTo) {
          const args = rule.target.map((key) => d[key]);
          if (args.every((arg) => arg !== null && arg !== undefined)) {
            // Delete the key/s that the rule applies to
            rule.target.forEach((key) => delete cloned[key]);
            // Assign the returned value to the cloned object
            Object.assign(cloned, rule.callback(...args));
          }
        }
      });
      // Convert camleCase keys to dash separated keys
      Object.keys(cloned).map((key) => {
        const dashSeparated = camelToDash(key);
        if (dashSeparated !== key) {
          Object.assign(cloned, { [dashSeparated]: cloned[key] });
          delete cloned[key];
        }
      });
      acc.push(cloned);
      return acc;
    }, []);
  },
  rules: [
    {
      appliesTo: "line",
      target: ["points"],
      callback: function (points) {
        return {
          x1: points[0].x,
          y1: points[0].y,
          x2: points[1].x,
          y2: points[1].y
        };
      }
    },
    {
      appliesTo: "line",
      target: ["from", "length", "slope"],
      callback: function (from, length, slope) {
        return {
          x1: from.x,
          y1: from.y,
          x2: from.x + length * Math.cos(slope),
          y2: from.y + length * Math.sin(slope)
        };
      }
    },
    {
      appliesTo: "line",
      target: ["from", "length", "angle"],
      callback: function (from, length, angle) {
        return {
          x1: from.x,
          y1: from.y,
          x2: from.x + length * Math.cos((angle * Math.PI) / 180),
          y2: from.y + length * Math.sin((angle * Math.PI) / 180)
        };
      }
    },
    {
      appliesTo: "line",
      target: ["center", "length", "slope"],
      callback: function (center, length, slope) {
        return {
          x1: center.x - 0.5 * length * Math.cos(slope),
          y1: center.y - 0.5 * length * Math.sin(slope),
          x2: center.x + 0.5 * length * Math.cos(slope),
          y2: center.y + 0.5 * length * Math.sin(slope)
        };
      }
    },
    {
      appliesTo: "line",
      target: ["center", "length", "angle"],
      callback: function (center, length, angle) {
        return {
          x1: center.x - 0.5 * length * Math.cos((angle * Math.PI) / 180),
          y1: center.y - 0.5 * length * Math.sin((angle * Math.PI) / 180),
          x2: center.x + 0.5 * length * Math.cos((angle * Math.PI) / 180),
          y2: center.y + 0.5 * length * Math.sin((angle * Math.PI) / 180)
        };
      }
    },
    {
      appliesTo: "polygon",
      target: ["points"],
      callback: function (points) {
        return {
          points: points.map((p) => [p.x, p.y].join(",")).join(" ")
        };
      }
    },
    {
      appliesTo: "text",
      target: ["rotate", "x", "y"],
      callback: function (rotate, x, y) {
        return {
          transform: `rotate(${rotate}, ${x}, ${y})`,
          x,
          y
        };
      }
    },
    {
      appliesTo: "path",
      target: ["points", "draw"],
      callback: function (points, draw) {
        return {
          d: draw(d3.path(), points)
        };
      }
    }
  ]
};

function renderBasicShapes(parent, data) {
  // Group by type {line: [], rect: [], ...}
  data
    .reduce(
      (entryMap, e) =>
        entryMap.set(e.type, [...(entryMap.get(e.type) || []), e]),
      new Map()
    )
    // Render each type
    .forEach((value, key) => {
      const selection = parent
        .selectAll(key) // key is an enumerable of basic svg elements tagNames
        .filter(function () {
          return this.parentNode === parent.node(); // Only immediate children
        })
        .data(value);
      selection.exit().remove();
      selection
        .enter()
        .append(key)
        .merge(selection)
        .each(function (d) {
          const selection = d3.select(this);
          if (d.type === "text") {
            selection.text(d.text);
          }
          if (d.type === "line" && !d.stroke) {
            d.stroke = "black";
          }
          for (const [key, value] of Object.entries(d)) {
            selection.attr(key, value);
          }
        });
    });
}

// For rotation about center, the center point of the group should be calculated. However,
// the bounding box of the group is not calculated before being rendered on the screen.
// To go around this issue, a solution is proposed here: See: https://talk.observablehq.com/t/getbbox/1222/3
// Nonetheless, it doesn't work for elements created using d3 itself and appended to a group!
// As such, calculate center of rotation manually for now
function getGroupTransformString(d) {
  if (typeof d.rotate === 'object') {
    return `translate(${d.x || 0}, ${d.y || 0}) rotate(${d.rotate.t || 0}, ${d.rotate.x || d.x || 0}, ${d.rotate.y || d.y || 0})`;
  } else {
    return `translate(${d.x || 0}, ${d.y || 0}) rotate(${d.rotate || 0})`;
  }
}

// Helper function to calculate line angle from two points
// eslint-disable-next-line no-unused-vars
function lineAngle(points) {
  const { m } = lineSlopeInterceptForm(points);
  return convRadToDeg(Math.atan(m));
}

function renderGroups(parent, data) {
  const selection = parent.selectAll("g").data(data);
  const exits = selection.exit();
  exits.remove();
  const enters = selection.enter().append("g");
  const all = enters.merge(selection);
  all
    .attr("id", (d) => d.id)
    .attr("class", (d) => d.class)
    .attr("transform", (d) => getGroupTransformString(d));
  all.each(function (d) {
    renderBasicShapes(d3.select(this), parser.parse(d.children));
  });
}

function lineSlopeInterceptForm(points) {
  const m = (points[1].y - points[0].y) / (points[1].x - points[0].x);
  const c = points[0].y - m * points[0].x;
  return { m, c };
}

function convRadToDeg(rad) {
  return (rad * 180) / Math.PI;
}

function transCoordsByMovingO(point, newOrigin) {
  return {
    x: point.x - newOrigin.x,
    y: point.y - newOrigin.y
  };
}

function transCoordsByAxesRot(point, angle) {
  const rad = (angle * Math.PI) / 180;
  return {
    x: point.x * Math.cos(rad) + point.y * Math.sin(rad),
    y: -point.x * Math.sin(rad) + point.y * Math.cos(rad)
  };
}

function segmentLength(points) {
  const dx = points[1].x - points[0].x;
  const dy = points[1].y - points[0].y;
  return Math.sqrt(dx * dx + dy * dy).toFixed(2);
}

function getStrokeWidth(d) {
  return d.strokeWidth || 0.5;
}

function genBasicDimLine(d) {
  const { m } = lineSlopeInterceptForm(d.points);
  const angle = convRadToDeg(Math.atan(m));
  const localPoints = d.points
    .map((p) => transCoordsByMovingO(p, d.points[0]))
    .map((p) => transCoordsByAxesRot(p, angle));
  const transMat = {
    dx: d.points[0].x,
    dy: d.points[0].y,
    dt: angle
  };
  const dimLineText = d.text || segmentLength(d.points);
  return {
    type: "group",
    translate: { x: transMat.dx, y: transMat.dy },
    rotate: transMat.dt,
    children: [
      // Main Line
      {
        type: "line",
        stroke: "black",
        x1: localPoints[0].x,
        y1: localPoints[0].y - 25,
        x2: localPoints[1].x,
        y2: localPoints[1].y - 25,
        strokeWidth: getStrokeWidth(d)
      },
      // Tick Lines
      {
        type: "line",
        stroke: "black",
        x1: localPoints[0].x,
        y1: localPoints[0].y - 25 - 10,
        x2: localPoints[0].x,
        y2: localPoints[1].y - 25 + 10,
        strokeWidth: getStrokeWidth(d)
      },
      {
        type: "line",
        stroke: "black",
        x1: localPoints[1].x,
        y1: localPoints[0].y - 25 - 10,
        x2: localPoints[1].x,
        y2: localPoints[1].y - 25 + 10,
        strokeWidth: getStrokeWidth(d)
      },
      // Text
      {
        type: "text",
        text: dimLineText,
        x: 0.5 * (localPoints[0].x + localPoints[1].x),
        y: 0.5 * (localPoints[0].y + localPoints[1].y) - 25 - 10
      }
    ]
  };
}

function genDistributedForce(d) {
  // Calculate the slope and c of the line: y = m * x + c
  const { m } = lineSlopeInterceptForm(d.points);
  const angle = convRadToDeg(Math.atan(m));
  // Find local coordinates of the points
  const lp = d.points
    .map((p) => transCoordsByMovingO(p, d.points[0]))
    .map((p) => transCoordsByAxesRot(p, angle));
  // Calculate the transformation matrix
  const transMat = {
    dx: d.points[0].x,
    dy: d.points[0].y,
    dt: angle
  };
  // Find the text to be shown at the center, left, and right sides of the distributed force
  const textCenter = d.texts.center || "";
  const textLeft = d.texts.left || "";
  const textRight = d.texts.right || "";
  // Number of force lines
  const n = d.numForceLines || 2;

  return {
    type: "group",
    x: transMat.dx,
    y: transMat.dy,
    rotate: transMat.dt,
    children: [
      // Bot Line
      {
        type: "line",
        stroke: "black",
        x1: lp[0].x,
        y1: lp[0].y - 25,
        x2: lp[1].x,
        y2: lp[1].y - 25,
        strokeWidth: getStrokeWidth(d)
      },
      // Top Line
      {
        type: "line",
        stroke: "black",
        x1: lp[0].x,
        y1: lp[0].y - 25 - d.heights[0],
        x2: lp[1].x,
        y2: lp[1].y - 25 - d.heights[1],
        strokeWidth: getStrokeWidth(d)
      },
      // Generate n lines between start and end points
      ...Array.from({ length: n }, (_, i) => {
        const t = n === 1 ? 0.5 : i / (n - 1);
        const x = lp[0].x + t * (lp[1].x - lp[0].x);
        const height = d.heights[0] + t * (d.heights[1] - d.heights[0]);
  
        return {
          type: "line",
          stroke: "black",
          x1: x,
          y1: lp[0].y - 25,
          x2: x,
          y2: lp[1].y - 25 - height,
          markerStart: height > 0 ? "url(#arrow)" : "",
          strokeWidth: getStrokeWidth(d)
        };
      }),
      // Text Center
      {
        type: "text",
        text: textCenter,
        x: 0.5 * (lp[0].x + lp[1].x) - 25,
        y: 0.5 * (lp[0].y + lp[1].y) - 25 + 20
      },
      // Text Left
      {
        type: "text",
        text: textLeft,
        x: lp[0].x - 15,
        y: 0.5 * (lp[0].y + lp[1].y) - 25 + 20,
        rotate: -90
      },
      // Text Right
      {
        type: "text",
        text: textRight,
        x: lp[1].x + 25,
        y: 0.5 * (lp[0].y + lp[1].y) - 25 + 20,
        rotate: -90
      }
    ]
  };
}

function render(selection, data) {
  // Filter the basic shapes and render them first
  const basicShapes = data.filter((d) => basicShapeTypes.includes(d.type));
  selection.call(renderBasicShapes, parser.parse(basicShapes));
  
  renderGroups(
    selection,
    data
      .filter((d) => d.type === "group")
      .concat(
        data.filter((d) => d.type === "dimLine").map((d) => genBasicDimLine(d))
      ).concat(
        data.filter((d) => d.type === "distributedForce").map((d) => genDistributedForce(d))
      )
  );
}

export default {
  name: "VisualizerComponent",
  props: {
    descriptor: {
      type: Array,
      required: true,
      default: () => []
    },
    width: {
      type: Number,
      required: true,
      default: () => 500
    },
    height: {
      type: Number,
      required: true,
      default: () => 500
    }
  },
  data() {
    return {
      stdPaths: {
        roller: "M 25 0 L 50 35 L 0 35 Z M 5 50 A 5 5 0 0 0 5 40 A 5 5 0 0 0 5 50 M 25 50 A 5 5 0 0 0 25 40 A 5 5 0 0 0 25 50 M 45 50 A 5 5 0 0 0 45 40 A 5 5 0 0 0 45 50",
        hinge: "M 25 0 L 50 50 L 0 50 Z",
        fixed: "M 25 0 L 25 50 M 25 0 L 12.5 12.5 M 25 12.5 L 12.5 25 M 25 25 L 12.5 37.5 M 25 37.5 L 12.5 50"
      },
      strokes: {
        line: "black",
        circle: "black",
        polygon: "black",
        rect: "black",
        dimLine: "black",
        path: "black",
        default: "black"
      },
      strokeWidths: {
        line: 1,
        circle: 1.5,
        polygon: 1.5,
        rect: 1.5,
        dimLine: 1,
        path: 1.5,
        default: 1
      },
      fills: {
        line: "transparent",
        circle: "transparent",
        polygon: "transparent",
        rect: "transparent",
        dimLine: "transparent",
        path: "transparent",
        default: "transparent"
      }
    };
  },
  watch: {
    descriptor: {
      handler() {
        this.renderVisualization();
      },
      deep: true
    }
  },
  mounted() {
    this.renderVisualization();
  },
  methods: {
    renderVisualization() {
      if (!this.$refs.svg || !this.descriptor) return;
      
      const svg = d3.select(this.$refs.svg);
      svg.selectAll("*").remove(); // Clear previous render
      
      // Add defs for markers and patterns
      const defs = svg.append("defs");
      defs.call(defineArrowHead);
      defs.call(defineDiagonalHatch);

      render(svg, this.descriptor);
    }
  }
};
</script>

<style scoped>
svg {
  border: none;
  background-color: transparent;
}
</style>